import React from 'react';
import {
  Alert,
  Amount,
  Avatar,
  Box,
  Button,
  Divider,
  DownloadIcon,
  EditIcon,
  Heading,
  Link,
  StepGroup,
  StepItem,
  StepItemIndicator,
  Text,
  useToast,
} from '@razorpay/blade/components';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { useFormWithProvider } from 'forms';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { FETCH_MY_LOANS } from '../constants';
import { useLoanSuccess } from './index';
import { CreateLoanRequestFormData } from './types';
import { formatMonthYear } from '../utils';

const ReviewRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box display="flex" justifyContent="space-between">
    <Text size="small" variant="body" weight="semibold" color="surface.text.gray.muted">
      {label}
    </Text>
    <Text size="small" variant="body" weight="regular" color="surface.text.gray.subtle">
      {value}
    </Text>
  </Box>
);

const ApplicationTimeline = ({ userName }: { userName: string }) => (
  <Box
    width="380px"
    height="100%"
    borderLeftWidth="thin"
    borderColor="surface.border.gray.subtle"
    backgroundColor="surface.background.gray.moderate"
    flexShrink={0}>
    <Box padding="spacing.7" display="flex" flexDirection="column" gap="spacing.6">
      <Text size="medium" weight="medium" variant="body" color="surface.text.gray.subtle">
        Application Timeline
      </Text>
      <StepGroup orientation="vertical" size="medium">
        <StepItem
          title="Disbursement of Amount"
          description="By Admin or Finance"
          marker={<StepItemIndicator color="neutral" />}
          stepProgress="none"
        />
        <StepItem
          title="Review & Approval"
          description="By Admin"
          marker={<StepItemIndicator color="neutral" />}
          stepProgress="none"
        />
        <StepItem
          title="Submit Request"
          description={`By ${userName} (You)`}
          marker={<StepItemIndicator color="primary" />}
          stepProgress="start"
        />
      </StepGroup>

      <Divider />
      <Box>
        <Text size="medium" weight="medium" color="surface.text.gray.subtle">
          Next Steps
        </Text>
        <Text size="small" weight="regular" color="surface.text.gray.muted" marginTop="spacing.3">
          Track all requests on the dashboard. For queries, contact your admin or designated
          approver.
        </Text>
      </Box>
    </Box>
  </Box>
);

const Review = () => {
  const { updateStepStatus, setCurrentStep, onSuccess } = useWizardInfo();
  const { formData } = useFormWithProvider<CreateLoanRequestFormData>();
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const userName = useSelector(loggedInUserSelectors.name) ?? 'You';
  const { setLoanId } = useLoanSuccess();

  const isEditMode = !!formData.editLoanId;

  const downloadDocumentMutation = useMutation({
    mutationFn: (fileId: string) => api.loans.downloadLoanDocument(fileId),
    onSuccess: (res) => {
      window.open(res.signed_url, '_blank');
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to open document. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const loanPayload = {
    principal: Number(formData.principal),
    tenure: formData.tenure ?? 1,
    recovery_start_from: formData.recoveryStartFrom ?? '',
    ...(formData.description ? { remarks: formData.description } : {}),
    ...(formData.documentFileId ? { document_file_id: formData.documentFileId } : {}),
  };

  const editLoanMutation = useMutation({
    mutationFn: () => api.loans.editEmployeeLoan(formData.editLoanId!, loanPayload),
    onSuccess: () => {
      queryClient.invalidateQueries(FETCH_MY_LOANS);
      updateStepStatus(2, 'success');
      onSuccess?.();
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to update request. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const createLoanMutation = useMutation({
    mutationFn: () =>
      api.loans.createEmployeeLoan({
        loan_category_id: formData.loanCategoryId!,
        ...loanPayload,
      }),
    onSuccess: (response: { id: number; status: string }) => {
      queryClient.invalidateQueries(FETCH_MY_LOANS);
      setLoanId(response.id);
      updateStepStatus(2, 'success');
      onSuccess?.();
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to submit request. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const activeMutation = isEditMode ? editLoanMutation : createLoanMutation;

  return (
    <Box display="flex" flex={1} overflow="hidden">
      <FormWrapper
        onSubmit={(e) => {
          e.preventDefault();
          activeMutation.mutate();
        }}
        style={{ flex: 1, minWidth: 0 }}>
        <StepContentWrapper noBgIllustration width="462px">
          <Box display="flex" flexDirection="column" gap="spacing.8">
            <Box>
              <Heading size="large" weight="semibold" color="surface.text.gray.normal">
                Review Request
              </Heading>
              <Text
                size="medium"
                color="surface.text.gray.muted"
                variant="body"
                weight="regular"
                marginTop="spacing.2">
                Review the request details to be sent for approval
              </Text>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              borderWidth="thin"
              borderRadius="medium"
              borderColor="surface.border.gray.muted"
              padding="spacing.7"
              gap="spacing.6">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom="spacing.7">
                <Box>
                  <Text
                    size="small"
                    variant="body"
                    weight="semibold"
                    color="surface.text.gray.muted">
                    Requested Amount
                  </Text>
                  <Amount value={Number(formData.principal || 0)} size="large" />
                </Box>
                <Link
                  color="primary"
                  size="medium"
                  onClick={() => setCurrentStep(1)}
                  icon={EditIcon}>
                  Edit
                </Link>
              </Box>
              {formData.categoryName && (
                <ReviewRow label="Reason for Request" value={formData.categoryName} />
              )}
              {formData.description && (
                <ReviewRow label="Additional Details" value={formData.description} />
              )}
              {formData.documentFileId && (
                <ReviewRow
                  label="Documents"
                  value={
                    <Link
                      icon={DownloadIcon}
                      iconPosition="left"
                      onClick={() => downloadDocumentMutation.mutate(formData.documentFileId!)}
                      size="small">
                      {downloadDocumentMutation.isLoading ? 'Loading...' : 'Download Document(s)'}
                    </Link>
                  }
                />
              )}
              {formData.tenure && (
                <ReviewRow
                  label="Repayment Tenure"
                  value={`${formData.tenure} Month${formData.tenure > 1 ? 's' : ''}`}
                />
              )}
              {formData.recoveryStartFrom && (
                <ReviewRow
                  label="Repayment Start Date"
                  value={formatMonthYear(formData.recoveryStartFrom)}
                />
              )}
            </Box>

            <Alert
              color="information"
              isDismissible={false}
              description="Approved terms (amount, rate, tenure) will be determined during review and may differ from your initial request."
            />
          </Box>
        </StepContentWrapper>

        <StepActionsWrapper width="462px">
          <Button variant="tertiary" onClick={() => setCurrentStep(1)}>
            Previous
          </Button>
          <Button type="submit" isLoading={activeMutation.isLoading}>
            {isEditMode ? 'Update Request' : 'Submit Request'}
          </Button>
        </StepActionsWrapper>
      </FormWrapper>

      <ApplicationTimeline userName={userName} />
    </Box>
  );
};

export default Review;
